import { useState, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Building2, Plus, Search, UserPlus } from "lucide-react";
import { api } from "@/lib/api-client";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import type { VendorStatus } from "@/types";

// Import the dialogs that live in VendorAssessmentsList.
// They are not exported from that file, so we re-use the same
// CreateVendorDialog defined locally below.  SendAssessmentDialog
// is kept in VendorAssessmentsList (it is launched from VendorProfile).
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { InputGroup } from "@/components/ui/Input";
import { toast } from "@/components/ui/Toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// ─────────────────────────────────────────────
// VendorsList
//
// Screen — /vendors
// Shows all vendors; click a row to open VendorProfile (/vendors/:id)
// Design: ITEM-014 fix — vendors list replaces vendor-assessments list
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

interface VendorContact {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
}

interface VendorRow {
  id: string;
  name: string;
  industry: string | null;
  location: string | null;
  status: VendorStatus;
  contacts: VendorContact[];
  _count: { assessments: number };
}

interface VendorsListResponse {
  data: VendorRow[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

interface VendorOption {
  id: string;
  name: string;
}

// ── CreateVendorDialog ─────────────────────────

function CreateVendorDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: (vendor: VendorOption) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [location, setLocation] = useState("");
  const [website, setWebsite] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  function resetForm() {
    setName("");
    setIndustry("");
    setLocation("");
    setWebsite("");
    setContactName("");
    setContactEmail("");
    setContactPhone("");
  }

  const mutation = useMutation({
    mutationFn: async (body: {
      name: string;
      industry?: string;
      location?: string;
      website?: string;
    }) => {
      const res = await api.post<{ data: { id: string; name: string } }>("/vendors", body);
      const vendor = res.data;

      if (contactName && contactEmail) {
        await api.post(`/vendors/${vendor.id}/contacts`, {
          name: contactName,
          email: contactEmail,
          phone: contactPhone || undefined,
          isPrimary: true,
        });
      }

      return vendor;
    },
    onSuccess: (vendor) => {
      // Invalidate both query keys so VendorAssessmentsList's SendAssessmentDialog
      // and VendorsList stay coherent.
      queryClient.invalidateQueries({ queryKey: ["vendors-list"] });
      toast.success(`"${vendor.name}" created`);
      onCreated?.(vendor);
      onClose();
      resetForm();
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to create vendor");
    },
  });

  function handleSubmit() {
    if (!name.trim()) {
      toast.error(t("vendor.createVendorDialog.vendorNameRequired"));
      return;
    }
    mutation.mutate({
      name: name.trim(),
      industry: industry.trim() || undefined,
      location: location.trim() || undefined,
      website: website.trim() || undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("vendor.addVendor")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <InputGroup
            label={t("vendor.createVendorDialog.vendorName")}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("vendor.createVendorDialog.vendorNamePlaceholder")}
          />
          <InputGroup
            label={t("vendor.industry")}
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder={t("vendor.createVendorDialog.industryPlaceholder")}
          />
          <InputGroup
            label={t("vendor.location")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("vendor.createVendorDialog.locationPlaceholder")}
          />
          <InputGroup
            label={t("vendor.website")}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder={t("vendor.createVendorDialog.websitePlaceholder")}
          />
          <div className="border-t border-border-default pt-2">
            <p className="mb-3 text-caption text-text-muted">
              {t("vendor.createVendorDialog.primaryContact")}
            </p>
            <div className="flex flex-col gap-3">
              <InputGroup
                label={t("vendor.createVendorDialog.contactName")}
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={t("vendor.createVendorDialog.contactNamePlaceholder")}
              />
              <InputGroup
                label={t("vendor.createVendorDialog.contactEmail")}
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={t("vendor.createVendorDialog.contactEmailPlaceholder")}
              />
              <InputGroup
                label={t("vendor.createVendorDialog.contactPhone")}
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder={t("vendor.createVendorDialog.contactPhonePlaceholder")}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} isLoading={mutation.isPending}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t("vendor.addVendor")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── VendorsList (main page) ────────────────────

export function VendorsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  // NOTE: the backend currently strips the `search` param, so sending it does
  // nothing but trigger redundant refetches. We fetch the page unfiltered and
  // filter client-side below (see `filteredRows`) as a safe interim. BACKEND
  // dependency: real server-side search is needed to search across all pages.
  const params = new URLSearchParams({ page: String(page), pageSize: "50" });

  const { data, isLoading, isError, refetch } = useQuery<VendorsListResponse>({
    queryKey: ["vendors-list", page],
    queryFn: () => api.get(`/vendors?${params.toString()}`),
  });

  const columns: ColumnDef<VendorRow>[] = [
    {
      key: "name",
      header: t("vendor.vendorProfile"),
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-control bg-hover">
            <Building2 className="h-4 w-4 text-text-muted" />
          </div>
          <span className="font-medium text-text-primary">{row.name}</span>
        </div>
      ),
    },
    {
      key: "contactName",
      header: t("vendor.createVendorDialog.contactName"),
      cell: (row) => {
        const primary = row.contacts[0];
        return primary ? (
          <span className="text-text-secondary">{primary.name}</span>
        ) : (
          <span className="text-text-muted">-</span>
        );
      },
    },
    {
      key: "contactEmail",
      header: t("vendor.createVendorDialog.contactEmail"),
      cell: (row) => {
        const primary = row.contacts[0];
        return primary ? (
          <span className="text-text-secondary">{primary.email}</span>
        ) : (
          <span className="text-text-muted">-</span>
        );
      },
    },
    {
      key: "assessmentCount",
      header: t("vendor.assessmentCount"),
      cell: (row) => (
        <span className="text-text-secondary">{row._count.assessments}</span>
      ),
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="vendor" status={row.status} />,
    },
  ];

  const rows = data?.data ?? [];
  const meta = data?.meta;

  // Client-side search over the already-fetched page (backend ignores `search`).
  const query = deferredSearch.trim().toLowerCase();
  const filteredRows = query
    ? rows.filter((v) => {
        const haystack = [
          v.name,
          v.industry ?? "",
          v.location ?? "",
          ...v.contacts.flatMap((c) => [c.name, c.email]),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
    : rows;

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-text-primary">
            {t("vendor.title")}
          </h1>
          <p className="mt-1 text-[14px] text-text-secondary">
            {t("vendor.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          {t("vendor.addVendor")}
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          placeholder={t("vendor.searchPlaceholder")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="h-9 w-full rounded-control border border-border-default bg-bg-input pl-9 pr-3 text-[14px] text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
        />
      </div>

      {/* Table — distinguish a load error from a genuinely empty list */}
      {isError && !isLoading ? (
        <div className="rounded-card border border-border-default bg-card">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load vendors"
            description={t("errors.generic")}
            action={{ label: t("common.refresh"), onClick: () => { refetch(); } }}
          />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredRows}
          isLoading={isLoading}
          onRowClick={(row) => navigate(`/vendors/${row.id}`)}
          emptyState={
            query
              ? { icon: Search, title: t("common.noResults") }
              : {
                  icon: Building2,
                  title: t("vendor.noVendors"),
                  description: t("vendor.noVendorsDesc"),
                  action: { label: t("vendor.addVendor"), onClick: () => setCreateOpen(true) },
                }
          }
          // Pagination reflects the server's full set; hide it while a client-side
          // search is active since we only filter the current page.
          page={query ? undefined : meta?.page}
          totalPages={query ? undefined : meta?.totalPages}
          totalItems={query ? undefined : meta?.total}
          pageSize={query ? undefined : meta?.pageSize}
          onPageChange={query ? undefined : setPage}
        />
      )}

      {/* Create vendor dialog */}
      <CreateVendorDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(vendor) => navigate(`/vendors/${vendor.id}`)}
      />
    </div>
  );
}
